import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ShieldCheck, CircleNotch, CheckCircle, MagnifyingGlass,
  FileText, GraduationCap, FileArrowUp, PaperPlaneTilt,
} from '@phosphor-icons/react';
import { API } from '@/config/api';

export default function InstitutionPreAuthorizationTab() {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [selfCompletedCountFromServer, setSelfCompletedCountFromServer] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('coe_only');
  const [downloadingAll, setDownloadingAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/institution/preauth/eligible`);
      setStudents(data?.students || []);
      setSelfCompletedCountFromServer(Number(data?.self_completed_count || 0));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load Pre-Authorization list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const statusOf = (s) => {
    if (s.pre_auth_self_completed_at) return 'self_completed';
    if (s.pre_auth_sent_at) return 'sent';
    if (!s.ea_signed) return 'coe_only';
    return 'ready';
  };

  const filtered = students
    .filter(s => statusFilter === 'all' || statusOf(s) === statusFilter)
    .filter(s => {
      if (!search.trim()) return true;
      const t = search.toLowerCase();
      return (s.full_name || '').toLowerCase().includes(t)
        || (s.email || '').toLowerCase().includes(t)
        || (s.recruiter_name || '').toLowerCase().includes(t);
    });

  const counts = {
    all: students.length,
    coe_only: students.filter(s => statusOf(s) === 'coe_only').length,
    ready: students.filter(s => statusOf(s) === 'ready').length,
    sent: students.filter(s => statusOf(s) === 'sent').length,
    self_completed: students.filter(s => statusOf(s) === 'self_completed').length,
  };

  const selfCompletedCount = selfCompletedCountFromServer;

  const handleDownloadAll = useCallback(async () => {
    if (!selfCompletedCount) return;
    setDownloadingAll(true);
    try {
      const res = await axios.get(`${API}/institution/preauth/download-all`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const stamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 13);
      const a = document.createElement('a');
      a.href = url;
      a.download = `preauth_signed_pdfs_${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const n = res.headers['x-preauth-pdf-count'] || selfCompletedCount;
      toast.success(`Downloaded ${n} Pre-Auth PDF${String(n) === '1' ? '' : 's'}`);
    } catch (err) {
      let msg = 'Could not build the ZIP download';
      if (err?.response?.data) {
        try {
          const text = await err.response.data.text();
          const j = JSON.parse(text);
          msg = j?.detail || msg;
        } catch (_) { /* ignore */ }
      }
      toast.error(msg);
    } finally {
      setDownloadingAll(false);
    }
  }, [selfCompletedCount]);

  return (
    <div className="space-y-6" data-testid="institution-preauth-tab">
      <header className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-lg bg-indigo-100 flex items-center justify-center">
          <ShieldCheck size={24} weight="duotone" className="text-indigo-700" />
        </div>
        <div className="flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-400">Pre-Authorization</p>
          <h2 className="text-2xl font-semibold tracking-tight text-gray-900 mt-1 leading-[1.1]" style={{ fontFamily: "'Libre Baskerville', serif" }}>
            COE students at your college
          </h2>
          <p className="text-gray-500 text-[13px] mt-2 max-w-2xl leading-relaxed">
            Students at your college with <span className="font-semibold text-emerald-700">COE received</span>. Those with <span className="font-semibold text-blue-700">EA signed</span> also show the Pre-Auth status and PDF; the rest show "COE received · EA not signed".
          </p>
        </div>
      </header>

      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <button
          type="button"
          onClick={() => setStatusFilter(statusFilter === 'coe_only' ? 'all' : 'coe_only')}
          className={`border rounded-lg p-4 text-left transition-all ${statusFilter === 'coe_only' ? 'border-amber-600 bg-amber-600 text-white' : 'border-amber-200 bg-amber-50/60 hover:border-amber-400'}`}
          data-testid="institution-preauth-filter-coe-only"
        >
          <p className={`text-[10px] uppercase font-semibold ${statusFilter === 'coe_only' ? 'text-amber-100' : 'text-amber-800'}`}>COE only</p>
          <p className={`text-2xl font-semibold mt-1 ${statusFilter === 'coe_only' ? 'text-white' : 'text-amber-900'}`}>{counts.coe_only}</p>
        </button>
        <button
          type="button"
          onClick={() => setStatusFilter(statusFilter === 'ready' ? 'all' : 'ready')}
          className={`border rounded-lg p-4 text-left transition-all ${statusFilter === 'ready' ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white hover:border-gray-400'}`}
          data-testid="institution-preauth-filter-ready"
        >
          <p className={`text-[10px] uppercase font-semibold ${statusFilter === 'ready' ? 'text-gray-300' : 'text-gray-500'}`}>Ready to send</p>
          <p className={`text-2xl font-semibold mt-1 ${statusFilter === 'ready' ? 'text-white' : 'text-gray-900'}`}>{counts.ready}</p>
        </button>
        <button
          type="button"
          onClick={() => setStatusFilter(statusFilter === 'sent' ? 'all' : 'sent')}
          className={`border rounded-lg p-4 text-left transition-all ${statusFilter === 'sent' ? 'border-blue-700 bg-blue-700 text-white' : 'border-blue-200 bg-blue-50/60 hover:border-blue-400'}`}
          data-testid="institution-preauth-filter-sent"
        >
          <p className={`text-[10px] uppercase font-semibold ${statusFilter === 'sent' ? 'text-blue-100' : 'text-blue-700'}`}>Pre-Auth email sent</p>
          <p className={`text-2xl font-semibold mt-1 ${statusFilter === 'sent' ? 'text-white' : 'text-blue-900'}`}>{counts.sent}</p>
        </button>
        <button
          type="button"
          onClick={() => setStatusFilter(statusFilter === 'self_completed' ? 'all' : 'self_completed')}
          className={`border rounded-lg p-4 text-left transition-all ${statusFilter === 'self_completed' ? 'border-emerald-700 bg-emerald-700 text-white' : 'border-emerald-200 bg-emerald-50/60 hover:border-emerald-400'}`}
          data-testid="institution-preauth-filter-self-completed"
        >
          <p className={`text-[10px] uppercase font-semibold ${statusFilter === 'self_completed' ? 'text-emerald-100' : 'text-emerald-700'}`}>Self-completed</p>
          <p className={`text-2xl font-semibold mt-1 ${statusFilter === 'self_completed' ? 'text-white' : 'text-emerald-900'}`}>{counts.self_completed}</p>
        </button>
      </section>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email or recruiter…"
            className="pl-9 h-10 text-xs"
            data-testid="institution-preauth-search"
          />
        </div>
        <Button
          type="button"
          onClick={handleDownloadAll}
          disabled={!selfCompletedCount || downloadingAll}
          className="h-10 text-xs bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-50"
          data-testid="institution-preauth-download-all-btn"
          title={selfCompletedCount
            ? `Download all ${selfCompletedCount} self-completed Pre-Auth PDF${selfCompletedCount === 1 ? '' : 's'} as a ZIP`
            : 'No self-completed PDFs yet'}
        >
          {downloadingAll
            ? (<><CircleNotch size={13} className="mr-1.5 animate-spin" /> Preparing…</>)
            : (<><FileArrowUp size={13} weight="bold" className="mr-1.5" /> Download All ({selfCompletedCount})</>)}
        </Button>
      </div>

      <div className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm" data-testid="institution-preauth-list">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Student</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Recruiter</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Program</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Lists</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-700">Signed PDF</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-500"><CircleNotch size={16} className="animate-spin inline mr-2" /> Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                  {students.length === 0 ? (
                    <div>
                      <p className="font-medium mb-1">No COE students at your college yet</p>
                      <p className="text-[11px]">Students appear here once admin adds them to the COE Received list (or moves them to the COE Sent pipeline stage).</p>
                    </div>
                  ) : 'No students match your search or filter.'}
                </td></tr>
              )}
              {!loading && filtered.map(s => {
                const eaSigned = !!s.ea_signed;
                const needsAction = eaSigned && !s.pre_auth_sent_at && !s.pre_auth_self_completed_at;
                return (
                <tr key={s.id} className={needsAction ? 'bg-amber-50/40' : ''} data-testid={`institution-preauth-row-${s.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{s.full_name}</div>
                    <div className="text-[10.5px] text-gray-500">{s.email}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{s.recruiter_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-700">{s.assigned_program_name || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9.5px] font-semibold rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200"><GraduationCap size={9} weight="fill" /> COE</span>
                      {eaSigned ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9.5px] font-semibold rounded-full bg-blue-50 text-blue-800 border border-blue-200"><FileText size={9} weight="fill" /> EA signed</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9.5px] font-semibold rounded-full bg-gray-50 text-gray-500 border border-gray-200"><FileText size={9} weight="fill" /> EA pending</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {s.pre_auth_self_completed_at ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200"><CheckCircle size={10} weight="fill" /> Self-completed</span>
                    ) : s.pre_auth_sent_at ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-blue-50 text-blue-800 border border-blue-200"><CheckCircle size={10} weight="fill" /> Email sent</span>
                    ) : !eaSigned ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-50 text-amber-900 border border-amber-200" title="Pre-Auth unlocks once the EA is signed.">COE received · EA not signed</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-50 text-amber-800 border border-amber-200">Pending</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {s.pre_auth_signed_file_id ? (
                      <a
                        href={`${API}/files/${s.pre_auth_signed_file_id}/download`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 px-2.5 h-8 text-xs font-semibold rounded-md bg-emerald-700 hover:bg-emerald-800 text-white"
                        data-testid={`institution-preauth-download-${s.id}`}
                        title={`Download signed declaration (completed ${(s.pre_auth_self_completed_at || '').slice(0, 10)})`}
                      >
                        <FileArrowUp size={11} weight="bold" /> Download PDF
                      </a>
                    ) : s.pre_auth_sent_at ? (
                      <span className="inline-flex items-center gap-1 px-2 h-8 text-[11px] font-semibold rounded-md bg-gray-100 text-gray-500 border border-gray-200" title={`Pre-Auth email sent ${(s.pre_auth_sent_at || '').slice(0, 10)}`}>
                        <PaperPlaneTilt size={11} weight="fill" /> Awaiting signature
                      </span>
                    ) : !eaSigned ? (
                      <span className="text-[10.5px] text-gray-400" title="Pre-Auth unlocks once the EA is signed.">—</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 h-8 text-[11px] font-semibold rounded-md bg-amber-50 text-amber-800 border border-amber-200">
                        Pending
                      </span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-gray-500">
        Admin manages the COE and EA Signed lists. If a student is missing from this list, ask admin to add them.
      </p>
    </div>
  );
}
